import { useMemo } from 'react';
import { getMultisigBalance, getTokenPrices, getTransactions } from 'api';
import BigNumber from 'bignumber.js';
import { useAtom } from 'jotai';
import {
  ftBalancesAtom,
  nextExecutedNonceAtom,
  nextNonceAtom,
  nftBalancesAtom,
  safeAtom,
  SafeFtBalance,
  SafeNftBalance,
  safeStxBalanceAtom,
  safeTotalBalanceAtom,
  safeTransactionsAtom,
} from 'store/safe';
import { NETWORK, StxChainEnum } from 'types/enums';
import { AssetListFromBalancesType, getAssetListFromBalances } from 'utils/asset-list';

import { STX_DECIMALS } from '../constants/base';
import nftList from '../constants/nft-list';
import { useTokenPrices } from '../store';
import { DetailedStxBalance, NftTokenMetadata, TokenAssetBase } from '../types';
import { getNfTInfo } from '../utils';
import { getHiroNonce } from '../utils/hiroApi';

import useAssets from './use-assets';
import useGetTokenMetadata from './use-getTokenMetadata'; // TODO_Y: have a look
import useNetwork from './use-network';
import useData from './useData';

// TODO_Y: have a look
// TODO_R: if useSafe is used only within SafeDetailsPage components tree, then move it into SafeDetailsPage/store.ts including the safeAtom too
const useSafe = () => {
  const [safe, setSafe] = useAtom(safeAtom);
  const [network, stacksNetwork] = useNetwork();
  const [, , , getFtAssets, getNFTAssets] = useAssets();
  const [nextNonce, setNextNonce] = useAtom(nextNonceAtom);
  const [nextExecutedNonce, setNextExecutedNonce] = useAtom(nextExecutedNonceAtom);
  const [stxBalance, setStxBalance] = useAtom(safeStxBalanceAtom);
  const [ftBalances, setFtBalances] = useAtom(ftBalancesAtom);
  const [nftBalances, setNftBalances] = useAtom(nftBalancesAtom);
  const [transactions, setTransactions] = useAtom(safeTransactionsAtom);
  const [totalBalance, setTotalBalance] = useAtom(safeTotalBalanceAtom);
  const { getMultisignWalletDataById } = useData();
  const { fetchTokenMetadata } = useGetTokenMetadata();
  const { tokenPrices, setTokenPrices, lastUpdated, setLastUpdated } = useTokenPrices();

  const loadTokenPrices = (): Promise<Record<string, number> | null> => {
    if (!Object.keys(tokenPrices).length || Date.now() - lastUpdated < 60e3) {
      const tokenPriceRecord: Record<string, number> = { ...tokenPrices };

      return getTokenPrices()
        .then((result) => {
          result.result.data.forEach(
            (tokenPrice) => (tokenPriceRecord[tokenPrice.contract_id] = tokenPrice.last_price_usd)
          );
          setTokenPrices(tokenPriceRecord);
          setLastUpdated(Date.now());

          return tokenPriceRecord;
        })
        .catch((err) => {
          console.error('Error loading token prices', err);

          return null;
        });
    } else return Promise.resolve(tokenPrices);
  };

  const loadNextNonce = (address: string): Promise<number> =>
    getHiroNonce(address, network as NETWORK)
      .then((res) => res.json())
      .then((res) => {
        const nonce = res.last_executed_tx_nonce;
        const nextNonceValue = nonce === null || nonce === undefined ? 0 : nonce + 1;
        setNextExecutedNonce(nextNonceValue);
        setNextNonce(res.possible_next_nonce);

        return res.possible_next_nonce;
      });

  const loadTransactions = (id: string) =>
    getTransactions(id).then((transactions) => {
      setTransactions(transactions);

      return transactions;
    });

  const loadAssetsBalances = async (
    address: string
  ): Promise<{
    ft: SafeFtBalance[];
    nft: SafeNftBalance[];
    stx: DetailedStxBalance;
    // stx: { available: number; locked: number };
  }> => {
    const safeBalances = await getMultisigBalance(address, StxChainEnum[network]);

    const safeStxBalance = safeBalances.result?.stx;
    const stxFtBalance = {
      available: new BigNumber(safeStxBalance.balance)
        .minus(safeStxBalance.locked)
        .shiftedBy(-STX_DECIMALS),
      locked: new BigNumber(safeStxBalance.locked).shiftedBy(-STX_DECIMALS),
    };
    setStxBalance(stxFtBalance);

    // Build fungible token balances
    // Start with STX
    const stxBalance: SafeFtBalance = {
      asset: {
        address: 'STX',
        symbol: 'STX',
        decimals: 6,
        ref: 'STX',
      },
      balance: (Number(safeStxBalance.balance) - Number(safeStxBalance.locked)).toString(),
    };

    const fungible_tokens = safeBalances.result?.fungible_tokens || [];
    const non_fungible_tokens = safeBalances.result?.non_fungible_tokens || [];

    return Promise.all([
      ...fungible_tokens.map((ftToken) => {
        const [fullTokenAddress, tokenRef] = ftToken.token.split('::');

        return fetchTokenMetadata(fullTokenAddress, tokenRef);
      }),
      ...non_fungible_tokens.map((nft) =>
        getNfTInfo(stacksNetwork, nft.token.split('::')[0], nft.token.split('::')[1])
      ),
    ]).then((response: (TokenAssetBase | NftTokenMetadata)[]) => {
      const ftMetadata = response.slice(0, fungible_tokens.length) as TokenAssetBase[];
      const nftMetadata = response.slice(fungible_tokens.length) as NftTokenMetadata[];

      const ftBalancesFromApi = ftMetadata.map(
        ({ address, symbol, decimals, ref, metadata }): SafeFtBalance => ({
          asset: {
            address,
            symbol,
            decimals,
            ref,
            metadata,
          },
          balance: fungible_tokens.find((x) => x.contract === address)?.balance || '',
        })
      );

      // User defined fungible tokens
      const ftBalancesCustom: SafeFtBalance[] = getFtAssets()
        .filter((x) => ftBalancesFromApi.find((y) => y.asset.address === x.address) === undefined)
        .map((x) => ({ asset: x, balance: '0' }));

      // Merge all fungible tokens. Append default token list in the end.
      const allFtBalances: SafeFtBalance[] = [
        stxBalance,
        ...ftBalancesFromApi,
        ...ftBalancesCustom,
      ];

      const nftBalancesFromApi = nftMetadata.map((nftMetadata, i): SafeNftBalance => {
        const balanceItem = non_fungible_tokens[i];

        return {
          asset: {
            address: balanceItem?.contract || '',
            name: nftMetadata?.metadata?.name || balanceItem?.contract.split('.')[1],
            ref: balanceItem?.contract.split('.')[1] || '',
            token: balanceItem?.token,
          },
          balance: balanceItem?.count || '',
          ids: [],
        };
      });

      const nftBalancesCustom: SafeNftBalance[] = getNFTAssets()
        .filter((x) => nftBalancesFromApi.find((y) => y.asset.address === x.address) === undefined)
        .map((a) => ({
          asset: a,
          balance: '0',
          ids: [],
        }));

      const allNftBalances: SafeNftBalance[] = [
        ...nftBalancesFromApi,
        ...nftBalancesCustom,
        ...nftList[network]
          .filter(
            (x) =>
              nftBalancesFromApi.find((y) => y.asset.address === x.address) === undefined &&
              nftBalancesCustom.find((y) => y.asset.address === x.address) === undefined
          )
          .map((x) => ({ asset: x, balance: '0', ids: [] })),
      ];

      setFtBalances(allFtBalances);
      setNftBalances(allNftBalances);

      return {
        ft: allFtBalances,
        nft: allNftBalances,
        stx: safeStxBalance,
      };
    });
  };

  // TODO_R: refactor this method and make it more readable
  const fetchSafeData = async (safeId: string, setIsLoading?: (status: boolean) => void) => {
    setIsLoading && setIsLoading(true);

    const backendSafe = await getMultisignWalletDataById(safeId);

    if (!backendSafe) {
      setIsLoading && setIsLoading(false);

      return;
    }

    const { _id, address, name, threshold, owners } = backendSafe;
    setSafe({ _id, address, name, threshold, owners, loading: false, page: 1 });
    setIsLoading && setIsLoading(false);

    loadTransactions(_id).then();
    loadNextNonce(address).then();

    Promise.all([loadAssetsBalances(address), loadTokenPrices()]).then((response) => {
      if (!response[0] || !response[1]) return;
      const { ft, stx } = response[0];
      const tokenPriceRecord = response[1];

      setTotalBalance(
        ft.reduce((acc, balanceItem) => {
          const price = tokenPriceRecord[balanceItem.asset.address];
          if (!price) return acc;

          return (
            acc + price * (Number(balanceItem.balance) / Math.pow(10, balanceItem.asset.decimals))
          );
        }, 0) +
          Number(stx.locked) / Math.pow(10, 6)
      );
    });
  };

  const assetList = useMemo(() => {
    const list = getAssetListFromBalances(ftBalances, []).filter((asset) => +asset.balance > 0);
    const sortedList = list
      .sort((a, b) => {
        const priceA = tokenPrices[a.address] || 0;
        const priceB = tokenPrices[b.address] || 0;

        return Number(b.balance) * priceB - Number(a.balance) * priceA;
      })
      .filter((asset) => Number(asset.balance) > 0);

    const stxAsset = sortedList.find((asset) => asset.symbol === 'STX');
    const sBtcAsset = sortedList.find((asset) => asset.symbol === 'sBTC');
    const pBtcAsset = sortedList.find((asset) => asset.symbol === 'pBTC');

    return [
      stxAsset,
      sBtcAsset,
      pBtcAsset,
      ...sortedList.filter((asset) => !['STX', 'sBTC', 'pBTC'].includes(asset.symbol)),
    ].filter(Boolean) as AssetListFromBalancesType[];
  }, [ftBalances]);

  return {
    safe,
    fetchSafeData,
    assetList,
    ftBalances,
    nftBalances,
    nextNonce,
    nextExecutedNonce,
    transactions,
    totalBalance,
    tokenPrices,
    stxBalance,
    loadTokenPrices,
  };
};

export default useSafe;
